import React from "react";
import { styled } from "@mui/system";
import { format as formatDate } from "date-fns";
import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
} from "@mui/material";

const StyledTableRow = styled(TableRow)(() => ({
  borderBottom: `1px solid #e0e0e0`,
  "&:hover": { backgroundColor: "#F5F5F5" },
  lineHeight: "1.0",
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  padding: theme.spacing(1, 0.5),
  fontWeight: 300,
  fontSize: theme.spacing(2),
  textAlign: "center",
  lineHeight: "1.0",
}));

const StyledHeaderCell = styled(StyledTableCell)(({ theme }) => ({
  backgroundColor: "#306FBC",
  border: "1px solid #ffffff",
  color: "#ffffff",
  fontWeight: 700,
  fontSize: theme.spacing(1.75),
  textAlign: "center",
  position: "sticky",
  top: 0,
  zIndex: 2,
  borderRight: "1px solid #306FBC",
}));

const StyledSubHeaderCell = styled(StyledTableCell)(({ theme }) => ({
  backgroundColor: "#306FBC",
  color: "#ffffff",
  fontWeight: 700,
  fontSize: theme.spacing(1.75),
  border: "1px solid #ffffff",
  padding: theme.spacing(1, 0.5),
  position: "sticky",
  top: theme.spacing(4.0),
  zIndex: 2,
}));

const DateCell = styled(StyledTableCell)(({ theme }) => ({
  fontSize: theme.spacing(1.75),
  fontWeight: 300,
  textAlign: "center",
  borderRight: "1px solid #a5a5a5ff",
}));

const PickupCell = styled(StyledTableCell)(({ pickup }) => ({
  fontWeight: 300,
  color: pickup < 0 ? "#CA3433" : "inherit",
  borderRight: "1px solid #a5a5a5ff",
}));

const TotalTableRow = styled(TableRow)(({ theme }) => ({
  backgroundColor: "#306FBC",
  borderTop: `2px solid #1e4a7a`,
  "&:hover": { backgroundColor: "#306FBC" },
  lineHeight: "1.0",
  position: "sticky",
  bottom: 0,
  zIndex: 1,
}));

const TotalCell = styled(TableCell)(({ theme }) => ({
  padding: theme.spacing(1, 0.5),
  fontWeight: 700,
  fontSize: theme.spacing(2),
  textAlign: "center",
  color: "#ffffff",
  border: "1px solid #ffffff",
}));

export const CombinedPickupTable = ({ data, fromDate, toDate, totals }) => {
  // Configuration for the three metric sections
  const metricsConfig = [
    {
      label: "Room Nights",
      dataKey: "roomNights",
      type: "rooms",
      totalsKey: "rooms",
    },
    {
      label: "ADR",
      dataKey: "averageRoomRates",
      type: "adr",
      totalsKey: "adr",
    },
    {
      label: "Revenue",
      dataKey: "revenueBudgets",
      type: "revenue",
      totalsKey: "revenue",
    },
  ];

  const formatValue = (value, type) => {
    if (value == null || value === -1) return "-";
    if (type === "revenue") return Math.round(value);
    if (type === "adr") return value.toFixed(2);
    return value;
  };

  const formatPickup = (pickup, type) => {
    if (pickup == null || pickup === -1) return "-";
    let formattedValue;
    if (type === "revenue") {
      formattedValue = Math.round(pickup);
    } else if (type === "adr") {
      formattedValue = pickup.toFixed(2);
    } else {
      formattedValue = pickup;
    }
    return pickup >= 0 ? `${formattedValue}` : formattedValue;
  };

  return (
    <TableContainer
      component={Paper}
      sx={{ boxShadow: 2, maxHeight: "calc(100vh - 200px)", overflow: "auto" }}
    >
      <Table
        size="small"
        sx={{ minWidth: 800, lineHeight: "1.25" }}
        stickyHeader
      >
        <TableHead>
          <TableRow>
            <StyledHeaderCell rowSpan={2}>Date</StyledHeaderCell>
            {metricsConfig.map((metric) => (
              <StyledHeaderCell key={metric.label} colSpan={3}>
                {metric.label}
              </StyledHeaderCell>
            ))}
          </TableRow>
          <TableRow>
            {metricsConfig.map((metric) => (
              <React.Fragment key={metric.label}>
                <StyledSubHeaderCell>
                  {formatDate(fromDate, "dd/MM")}
                </StyledSubHeaderCell>
                <StyledSubHeaderCell>
                  {formatDate(toDate, "dd/MM")}
                </StyledSubHeaderCell>
                <StyledSubHeaderCell>Pickup</StyledSubHeaderCell>
              </React.Fragment>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {data &&
            data.roomNights &&
            data.roomNights.map((row, idx) => (
              <StyledTableRow key={idx}>
                <DateCell>
                  {formatDate(new Date(row.date), "dd/MM")}
                  {formatDate(new Date(row.date), "EEE")}
                </DateCell>
                {metricsConfig.map((metric) => {
                  const rowData = data[metric.dataKey][idx];
                  return (
                    <React.Fragment key={metric.label}>
                      <StyledTableCell>
                        {formatValue(rowData?.fromData, metric.type)}
                      </StyledTableCell>
                      <StyledTableCell>
                        {formatValue(rowData?.toData, metric.type)}
                      </StyledTableCell>
                      <PickupCell pickup={rowData?.pickUp}>
                        {formatPickup(rowData?.pickUp, metric.type)}
                      </PickupCell>
                    </React.Fragment>
                  );
                })}
              </StyledTableRow>
            ))}
          {totals && (
            <TotalTableRow>
              <TotalCell>Total</TotalCell>
              {metricsConfig.map((metric) => {
                const totalData = totals[metric.totalsKey];
                return (
                  <React.Fragment key={metric.label}>
                    <TotalCell>
                      {formatValue(totalData.from, metric.type)}
                    </TotalCell>
                    <TotalCell>
                      {formatValue(totalData.to, metric.type)}
                    </TotalCell>
                    <TotalCell>
                      {formatPickup(totalData.pickup, metric.type)}
                    </TotalCell>
                  </React.Fragment>
                );
              })}
            </TotalTableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
